import { ArrowLeft, Eye, Mail, Pencil, RotateCw, UserPlus, X } from "lucide-react";
import { startTransition, useCallback, useEffect, useId, useState } from "react";
import type { FormEvent } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router";

import {
  ApiError,
  getMe,
  getPlatformOrg,
  invitePlatformOwner,
  openStaffView,
  rereadOrgKnowledge,
  resendPlatformInvite,
  revokePlatformInvite,
  setPlatformLicence,
} from "@/api/client";
import type { PlatformLicenceSet, PlatformOrgDetail, Role } from "@/api/client";
import { useAuth } from "@/auth/AuthProvider";
import { Field, SELECT, aYearOn, isoDay } from "@/screens/platform/fields";
import { LICENCE, day } from "@/screens/platform/licence";
import type { Licence } from "@/screens/platform/licence";
import { Badge } from "@/ui/Badge";
import { Button } from "@/ui/Button";
import { Dialog } from "@/ui/Dialog";
import { Input } from "@/ui/Input";
import { Notice } from "@/ui/Notice";
import { PageHeader } from "@/ui/PageHeader";
import { Card, Skeleton, Spinner } from "@/ui/primitives";
import { Table, Td, Th } from "@/ui/Table";

const ROLE_LABEL: Record<Role, string> = {
  owner: "Owner",
  admin: "Admin",
  estimator: "Estimator",
  viewer: "Viewer",
};

function failure(err: unknown): string {
  return err instanceof ApiError ? err.detail : "Could not reach the server.";
}

function InviteOwnerDialog({
  orgId,
  onClose,
  onSent,
}: {
  orgId: string;
  onClose: () => void;
  onSent: (email: string) => void;
}) {
  const id = useId();
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await invitePlatformOwner(orgId, email.trim());
      onSent(email.trim());
    } catch (err) {
      setError(failure(err));
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()} label="Invite an owner">
      <form className="space-y-4 p-4" onSubmit={(event) => void submit(event)}>
        <p className="text-sm text-ink-2">
          They get an email with a link to choose their name and password, and join as an owner.
        </p>
        {error ? <Notice tone="danger">{error}</Notice> : null}
        <div className="space-y-1.5">
          <label htmlFor={id} className="block text-sm font-medium text-ink">
            Email
          </label>
          <Input
            id={id}
            type="email"
            required
            autoFocus
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
        </div>
        <div className="flex justify-end gap-2">
          <Button type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" disabled={busy || !email.trim()}>
            {busy ? <Spinner className="size-4" /> : <Mail aria-hidden="true" />}
            Send invite
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

/** The licence as the API takes it back, with another status. */
function asSet(licence: Licence, status: PlatformLicenceSet["status"]): PlatformLicenceSet {
  return {
    seats: licence.seats,
    starts: licence.starts_at.slice(0, 10),
    ends: licence.ends_at.slice(0, 10),
    status,
  };
}

function LicenceDialog({
  orgId,
  licence,
  used,
  onClose,
  onSaved,
}: {
  orgId: string;
  licence: Licence | null;
  /** Members plus pending invites. */
  used: number;
  onClose: () => void;
  onSaved: (detail: PlatformOrgDetail) => void;
}) {
  const today = new Date();
  const [status, setStatus] = useState<"trial" | "active">(
    licence?.status === "active" ? "active" : "trial",
  );
  const [seats, setSeats] = useState(String(licence?.seats ?? 3));
  const [starts, setStarts] = useState(licence ? licence.starts_at.slice(0, 10) : isoDay(today));
  const [ends, setEnds] = useState(licence ? licence.ends_at.slice(0, 10) : aYearOn(today));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const label = licence ? "Edit the licence" : "Set a licence";

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      onSaved(await setPlatformLicence(orgId, { seats: Number(seats), starts, ends, status }));
    } catch (err) {
      setError(failure(err));
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()} label={label}>
      <form className="space-y-4 p-4" onSubmit={(event) => void submit(event)}>
        {error ? <Notice tone="danger">{error}</Notice> : null}
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Licence">
            <select
              className={SELECT}
              value={status}
              onChange={(event) => setStatus(event.target.value as "trial" | "active")}
            >
              <option value="trial">Trial</option>
              <option value="active">Active</option>
            </select>
          </Field>
          <Field label="Seats" hint="Members plus pending invites.">
            <Input
              type="number"
              required
              min={1}
              max={10000}
              value={seats}
              onChange={(event) => setSeats(event.target.value)}
            />
          </Field>
          <Field label="Starts">
            <Input
              type="date"
              required
              value={starts}
              onChange={(event) => setStarts(event.target.value)}
            />
          </Field>
          <Field label="Ends" hint="Past this day the organization is read-only.">
            <Input
              type="date"
              required
              min={starts}
              value={ends}
              onChange={(event) => setEnds(event.target.value)}
            />
          </Field>
        </div>
        {seats !== "" && Number(seats) < used ? (
          <Notice tone="warn">
            {used === 1 ? "1 seat is" : `${used} seats are`} in use. Nobody loses access, and nobody
            more can join until seats free up.
          </Notice>
        ) : null}
        <div className="flex justify-end gap-2">
          <Button type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" disabled={busy}>
            {busy ? <Spinner className="size-4" /> : null}
            Save
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

/** Suspend or reactivate, once staff have read what it does. */
function SwitchDialog({
  orgId,
  orgName,
  licence,
  onClose,
  onSaved,
}: {
  orgId: string;
  orgName: string;
  licence: Licence;
  onClose: () => void;
  onSaved: (detail: PlatformOrgDetail) => void;
}) {
  const suspending = licence.status !== "suspended";
  const action = suspending ? "Suspend" : "Reactivate";
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const confirm = async () => {
    setBusy(true);
    setError(null);
    try {
      onSaved(await setPlatformLicence(orgId, asSet(licence, suspending ? "suspended" : "active")));
    } catch (err) {
      setError(failure(err));
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()} label={`${action} ${orgName}?`}>
      <div className="space-y-4 p-4">
        {error ? <Notice tone="danger">{error}</Notice> : null}
        <p className="text-sm text-ink-2">
          {suspending
            ? "Everyone in it is signed out and cannot sign in, and its invites cannot be accepted, until you reactivate it. Nothing is deleted."
            : "Its members can sign in again and its invites can be accepted. The licence becomes active."}
        </p>
        <div className="flex justify-end gap-2">
          <Button type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant={suspending ? "danger" : "primary"}
            disabled={busy}
            onClick={() => void confirm()}
          >
            {busy ? <Spinner className="size-4" /> : null}
            {action}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}

/**
 * One customer organization, for Darkstar staff (Phase 6 Task 8, ADR-0015): its licence,
 * who is in it and who is invited. Staff invite an owner, change the licence, suspend or
 * reactivate it, and view its workspace read-only (ADR-0018).
 */
export function OrganizationScreen() {
  const { id = "" } = useParams();
  const licenceId = useId();
  const [data, setData] = useState<PlatformOrgDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Straight after "New organization", the first owner's invite has just gone.
  const invited = (useLocation().state as { invited?: string } | null)?.invited;
  const [notice, setNotice] = useState<string | null>(
    invited ? `Created. Invite sent to ${invited}.` : null,
  );
  const [open, setOpen] = useState<"invite" | "licence" | "switch" | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const { adopt } = useAuth();
  const navigate = useNavigate();
  const [viewing, setViewing] = useState(false);

  // Their workspace, read-only, for a while (ADR-0018): this session now acts for them.
  const view = async () => {
    setViewing(true);
    try {
      await openStaffView(id);
      const who = await getMe();
      // One render for both: the router navigates in a transition (see AppShell).
      startTransition(() => {
        adopt(who);
        void navigate("/sets");
      });
    } catch (err) {
      setError(failure(err));
      setViewing(false);
    }
  };

  const [rereading, setRereading] = useState(false);

  // For a pilot whose past take-offs were read by older code: no one at the customer has
  // to press "Read again". Nothing is confirmed for them.
  const reread = async () => {
    setRereading(true);
    setNotice(null);
    try {
      const got = await rereadOrgKnowledge(id);
      const standard = got.standard_parts
        ? `Code standard: ${got.standard_parts} parts, ${got.standard_status}.`
        : "Code standard: none could be inferred from their codes.";
      const sources =
        got.sources === 0
          ? "They have no past take-offs or legends to read."
          : `${got.sources} ${got.sources === 1 ? "source is" : "sources are"} being read again; view the workspace in a minute to see what they taught.`;
      setNotice(`${standard} ${sources}`);
    } catch (err) {
      setError(failure(err));
    } finally {
      setRereading(false);
    }
  };

  const load = useCallback(async () => {
    try {
      setData(await getPlatformOrg(id));
      setError(null);
    } catch (err) {
      setError(failure(err));
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  const act = async (inviteId: string, call: () => Promise<unknown>, done: string) => {
    setBusyId(inviteId);
    setNotice(null);
    try {
      await call();
      await load();
      setNotice(done);
    } catch (err) {
      setError(failure(err));
    } finally {
      setBusyId(null);
    }
  };

  const back = (
    <Link to="/platform/orgs" className="inline-flex items-center gap-1 text-sm text-muted">
      <ArrowLeft aria-hidden="true" className="size-4" />
      Organizations
    </Link>
  );

  if (data === null) {
    return (
      <main className="mx-auto w-full max-w-6xl px-4 py-8 lg:px-8">
        {back}
        {error ? (
          <Notice tone="danger" className="mt-4">
            {error}
          </Notice>
        ) : (
          <div className="mt-6 space-y-2" role="status" aria-label="Loading the organization">
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </div>
        )}
      </main>
    );
  }

  const org = data.organization;
  const licence = org.licence;
  return (
    <main className="mx-auto w-full max-w-6xl px-4 py-8 lg:px-8">
      {back}
      <div className="mt-3">
        <PageHeader
          title={org.name}
          description={org.slug}
          actions={
            <>
              <Button disabled={viewing} onClick={() => void view()}>
                <Eye aria-hidden="true" />
                View workspace
              </Button>
              <Button disabled={rereading} onClick={() => void reread()}>
                <RotateCw aria-hidden="true" />
                Read knowledge again
              </Button>
              <Button variant="primary" onClick={() => setOpen("invite")}>
                <UserPlus aria-hidden="true" />
                Invite an owner
              </Button>
            </>
          }
        />
      </div>
      {error ? (
        <Notice tone="danger" className="mt-4">
          {error}
        </Notice>
      ) : null}
      {notice ? (
        <Notice tone="ok" className="mt-4">
          {notice}
        </Notice>
      ) : null}

      <div className="mt-6 space-y-8">
        <Card className="p-5">
          <section aria-labelledby={licenceId} className="space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h2 id={licenceId} className="text-sm font-semibold text-ink">
                Licence
              </h2>
              <div className="flex gap-2">
                {licence?.status !== "suspended" ? (
                  <Button size="sm" onClick={() => setOpen("licence")}>
                    <Pencil aria-hidden="true" />
                    {licence ? "Edit licence" : "Set a licence"}
                  </Button>
                ) : null}
                {licence ? (
                  <Button size="sm" onClick={() => setOpen("switch")}>
                    {licence.status === "suspended" ? "Reactivate" : "Suspend"}
                  </Button>
                ) : null}
              </div>
            </div>
            {licence?.status === "suspended" ? (
              <p className="text-sm text-ink-2">
                Nobody in it can sign in, and its invites cannot be accepted.
              </p>
            ) : null}
            {licence ? (
              <dl className="grid gap-3 text-sm sm:grid-cols-3">
                <div>
                  <dt className="text-xs text-muted">Status</dt>
                  <dd>
                    <Badge tone={LICENCE[licence.status].tone}>
                      {LICENCE[licence.status].label}
                    </Badge>
                  </dd>
                </div>
                <div>
                  <dt className="text-xs text-muted">Seats used</dt>
                  <dd className="text-ink">{`${org.members + org.pending_invites} of ${licence.seats}`}</dd>
                </div>
                <div>
                  <dt className="text-xs text-muted">Term</dt>
                  <dd className="text-ink">
                    {day(licence.starts_at)} to {day(licence.ends_at)}
                  </dd>
                </div>
              </dl>
            ) : (
              <p className="text-sm text-muted">
                No licence is recorded, so there is no seat limit or end date.
              </p>
            )}
          </section>
        </Card>

        <section className="space-y-2">
          <h2 className="text-sm font-semibold text-ink">Members</h2>
          {data.members.length === 0 ? (
            <p className="text-sm text-muted">Nobody has joined yet.</p>
          ) : (
            <Table aria-label="Members">
              <thead>
                <tr>
                  <Th>Name</Th>
                  <Th className="hidden md:table-cell">Email</Th>
                  <Th>Role</Th>
                  <Th className="hidden lg:table-cell">Last signed in</Th>
                </tr>
              </thead>
              <tbody>
                {data.members.map((m) => (
                  <tr key={m.user_id}>
                    <Td className="font-medium text-ink">
                      {m.name || m.email}
                      {m.status === "deactivated" ? (
                        <Badge tone="neutral" className="mt-1 flex w-fit">
                          Deactivated
                        </Badge>
                      ) : null}
                    </Td>
                    <Td className="hidden text-ink-2 md:table-cell">{m.email}</Td>
                    <Td className="text-ink-2">{ROLE_LABEL[m.role]}</Td>
                    <Td className="hidden text-ink-2 lg:table-cell">{day(m.last_login_at)}</Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </section>

        {data.invites.length > 0 ? (
          <section className="space-y-2">
            <h2 className="text-sm font-semibold text-ink">Pending invites</h2>
            <Table aria-label="Pending invites">
              <thead>
                <tr>
                  <Th>Email</Th>
                  <Th>Role</Th>
                  <Th className="hidden md:table-cell">Invited by</Th>
                  <Th className="hidden md:table-cell">Expires</Th>
                  <Th className="w-0">
                    <span className="sr-only">Actions</span>
                  </Th>
                </tr>
              </thead>
              <tbody>
                {data.invites.map((i) => (
                  <tr key={i.id}>
                    <Td className="text-ink">{i.email}</Td>
                    <Td className="text-ink-2">{ROLE_LABEL[i.role]}</Td>
                    <Td className="hidden text-ink-2 md:table-cell">
                      {i.invited_by ?? "Darkstar"}
                    </Td>
                    <Td className="hidden text-ink-2 md:table-cell">
                      {i.expired ? <Badge tone="warn">Expired</Badge> : day(i.expires_at)}
                    </Td>
                    <Td className="whitespace-nowrap">
                      <div className="flex justify-end gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          disabled={busyId === i.id}
                          onClick={() =>
                            void act(
                              i.id,
                              () => resendPlatformInvite(org.id, i.id),
                              `Invite sent again to ${i.email}.`,
                            )
                          }
                        >
                          <RotateCw aria-hidden="true" />
                          Resend
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          disabled={busyId === i.id}
                          onClick={() =>
                            void act(
                              i.id,
                              () => revokePlatformInvite(org.id, i.id),
                              `Invite to ${i.email} revoked.`,
                            )
                          }
                        >
                          <X aria-hidden="true" />
                          Revoke
                        </Button>
                      </div>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </section>
        ) : null}
      </div>

      {open === "invite" ? (
        <InviteOwnerDialog
          orgId={org.id}
          onClose={() => setOpen(null)}
          onSent={(email) => {
            setOpen(null);
            setNotice(`Invite sent to ${email}.`);
            void load();
          }}
        />
      ) : null}
      {open === "licence" ? (
        <LicenceDialog
          orgId={org.id}
          licence={licence}
          used={org.members + org.pending_invites}
          onClose={() => setOpen(null)}
          onSaved={(detail) => {
            setOpen(null);
            setData(detail);
            setNotice("Licence saved.");
          }}
        />
      ) : null}
      {open === "switch" && licence ? (
        <SwitchDialog
          orgId={org.id}
          orgName={org.name}
          licence={licence}
          onClose={() => setOpen(null)}
          onSaved={(detail) => {
            setOpen(null);
            setData(detail);
            setNotice(
              detail.organization.licence?.status === "suspended"
                ? `Suspended. Nobody in ${org.name} can sign in.`
                : "Reactivated. Members can sign in again.",
            );
          }}
        />
      ) : null}
    </main>
  );
}
